import { ButtonContactSupport } from "@components/ButtonContactSupport";
import { ButtonLink } from "@components/ButtonLink";
import React from "react";

export type ErrorMessageRule = {
  pattern: string | RegExp;
  /** A string message may use `$1`-style backreferences to the pattern. */
  message: React.ReactNode;
};

export type ErrorMapEntry = {
  /** Matched against `problem.detail` for API errors, `error.message` otherwise. */
  byRegex?: ErrorMessageRule[];
  /** Keyed by `problem.code`; a nested map is keyed by `problem.detailCode`
   * (exact value, then its reason segment, then `Other`). */
  byCode?: Partial<
    Record<
      string,
      React.ReactNode | { [detailCodeOrReason: string]: React.ReactNode }
    >
  >;
  default?: React.ReactNode;
};

const arPayerPaymentMethodRequiredRegex: ErrorMessageRule = {
  pattern: "Wingspan requires a payment method to be in place when removing",
  message: (
    <>
      Wingspan requires a payment method to be in place when removing a previous
      one. If there is a specific issue with this, please reach out to the party
      you are trying to pay or Wingspan support at{" "}
      <a href="mailto:ARsupport@wingspan.app">ARsupport@wingspan.app</a>.
    </>
  )
};

export const errorMap = {
  Other: {},
  CardActions: {
    byRegex: [arPayerPaymentMethodRequiredRegex]
  },
  AddPaymentMethod: {
    byRegex: [arPayerPaymentMethodRequiredRegex]
  },
  UploadFile: {
    byCode: {
      // legacy: AlreadyExists
      ResourceConflict: {
        FilenameAlreadyExists: "Bummer, dude!",
        Other: "Sorry, file already exists!"
      },
      // legacy: MaxSizeExceeded
      ValidationError: {
        MaxFileSizeExceeded: "Sorry, files have a maximum size of 100MB"
      }
    },
    default: (
      <>
        Sorry, something went wrong during the upload, please try again or{" "}
        <ButtonContactSupport>contact our support</ButtonContactSupport>
      </>
    )
  },
  BrowsingFiles: {
    byCode: {
      // No canonical v3 code exists for 500s (the status is undocumented in
      // the spec); kept under the legacy name in case the server echoes it.
      InternalServerError:
        "Sorry, our file vault appears to be down. Please reload!"
    }
  },
  DownloadFile: {
    byCode: {
      // legacy: NotFound
      ResourceNotFound: {
        FileNotFound:
          "Sorry, we seem to have misplaced the file you're trying to download"
      }
    }
  },
  SettingMemberLogo: {
    byCode: {
      // legacy: AlreadyExists
      ResourceConflict: {
        FilenameAlreadyExists: "Please change the filename on your image file"
      }
    }
  },
  VerifyPhone: {
    byRegex: [
      {
        pattern: "Phone number is not valid",
        message: (
          <>Phone number is not valid. Please enter a valid phone number.</>
        )
      },
      {
        pattern: "Max send attempts reached",
        message: "You have reached the maximum number of send attempts."
      },
      {
        pattern: "SMS is not supported by landline phone number",
        message: "SMS is not supported by landline phone number."
      },
      {
        pattern: "The verification code or phone you've entered is incorrect",
        message: "The verification code or phone you've entered is incorrect"
      }
    ]
  },
  CreateOtpSession: {
    byRegex: [
      {
        pattern: "The verification code or phone you've entered is incorrect",
        message: (
          <>
            That code was invalid. Please try again. If this persists please{" "}
            <ButtonContactSupport>contact our support</ButtonContactSupport>
          </>
        )
      }
    ]
  },
  PendingWithholding: {
    byRegex: [
      {
        pattern: "has no payout account",
        message: (
          <>
            You don't currently have a payout account. Please{" "}
            <ButtonLink to="/member/settings/payment-methods">
              update your payout
            </ButtonLink>{" "}
            account to withdraw.
          </>
        )
      },
      {
        pattern: "Please reconnect your linked account",
        message: (
          <>
            Please try to reconnect an deposit account in settings. If this
            persists please{" "}
            <ButtonContactSupport>contact our support</ButtonContactSupport>
          </>
        )
      },
      {
        pattern: "No linked accounts available for deposit",
        message: (
          <>
            No linked accounts available for deposit. Please try to link an
            deposit account first. If this persists please{" "}
            <ButtonContactSupport>contact our support</ButtonContactSupport>
          </>
        )
      }
    ]
  },
  Auth: {
    byRegex: [
      {
        pattern: "Sorry! We can't find that email address",
        message: "Sorry! We can't find that email address."
      },
      {
        pattern: "User for email already exists",
        message:
          "An account with this email already exists. Please log in or use the password recovery feature if you've forgotten your password."
      },
      {
        pattern:
          "Sorry! The password you entered does not match what we have on file.",
        message: (
          <>
            Sorry! Looks like this email is already registered, but the password
            you submitted is invalid. Please use
            <ButtonLink to="/member/reset-password">reset password</ButtonLink>
            if necessary.
          </>
        )
      },
      {
        pattern: "Wingspan is invite only",
        message: (
          <>
            Sorry! Wingspan is an invite only platform. If you think this is an
            error, please{" "}
            <ButtonLink to="/member/invitation/resend">
              request a new invite link
            </ButtonLink>{" "}
            to continue.
          </>
        )
      },
      {
        pattern: "The invite code provided is invalid or has expired",
        message: (
          <>
            Sorry! That invite code is invalid or expired. Please
            <ButtonLink to="#help-me">try another way</ButtonLink>
            to access.
          </>
        )
      },
      {
        pattern: "Email does not match the invitation email",
        message: (
          <>
            The email address you entered doesn't match our records. Please
            check that you're using the exact email address provided in your
            invitation. If you continue to have trouble, click 'Not your email?'
            below for assistance.
          </>
        )
      }
    ]
  },
  InvoiceForm: {
    byRegex: [
      {
        pattern: "Can't calculate the total amount.",
        message:
          "Can't calculate the total amount. Please try to complete all Rate fields"
      },
      {
        pattern:
          "Invoices submitted to this client will be declined automatically",
        message:
          "This client does not accept inbound invoices. Please reach out to them for assistance."
      },
      {
        pattern: "does not allow invoice submissions",
        message: "Client does not allow invoice submissions."
      }
    ]
  },
  UpdateSeriesInvoice: {
    byRegex: [
      {
        pattern: "updating Completed scheduleDate not possible",
        message: "Sorry! You can't update completed invoice."
      }
    ]
  },
  BankCard: {
    byRegex: [
      {
        pattern: "Card number not valid",
        message: "Card number not valid"
      },
      {
        pattern: "Only Mastercard and Visa supported",
        message: "Only Mastercard and Visa supported"
      },
      {
        pattern: "Debit Card creation failed for member",
        message: (
          <>
            This card is not valid. Either the information is incorrect, it is
            not a debit card, or you've already added it. Please check and try
            again. If this persists please{" "}
            <ButtonContactSupport>contact our support</ButtonContactSupport>
          </>
        )
      },
      {
        pattern: "Your card was declined",
        message: "Your card was declined"
      }
    ]
  },
  Deduction: {
    byRegex: [
      {
        pattern: "Cannot delete a deduction that has been partially applied",
        message: "Cannot delete a deduction that has been partially applied"
      }
    ]
  },
  VerifyEmail: {
    byRegex: [
      {
        pattern: "Email is already in use",
        message: "Email is already in use"
      }
    ]
  },
  PayInvoice: {
    byRegex: [
      {
        pattern: "Error accepting card information: Your card was declined.",
        message:
          "Your card was declined. Please try again or contact your bank."
      },
      {
        pattern:
          "Your payment method was declined while charging for subscription",
        message:
          "Your card was declined. Please try again or contact your bank."
      },
      {
        pattern: "The bank returned the decline code `insufficient_funds`.",
        message: "Insufficient funds."
      },
      {
        pattern: "The bank returned the decline code",
        message: "Your card was declined."
      },
      {
        pattern: "Cannot pay invoice, account",
        message:
          "The referenced account is not active, please select a new one."
      },
      {
        pattern: "Counterparty couldn't be created for invoice",
        message: (
          <>
            Your account numbers are not good to complete the payment. Please,{" "}
            <ButtonContactSupport>contact our support</ButtonContactSupport>
          </>
        )
      },
      {
        pattern: "Counterparty and Token couldn't be created for invoice",
        message: (
          <>
            Your account numbers are not good to complete the payment. Please,{" "}
            <ButtonContactSupport>contact our support</ButtonContactSupport>
          </>
        )
      },
      {
        pattern: "Paid invoices cannot be updated.",
        message: "Invoice is already paid."
      },
      {
        pattern: "Oops! Some of your credit card info is missing or incorrect.",
        message: "Oops! Some of your credit card info is missing or incorrect."
      },
      {
        pattern: "Account with ID (.*) does not exist or cannot be used",
        message: "This account does not exist or cannot be used."
      }
    ]
  },
  NEC1099TaxDocumentsDownload: {
    byRegex: [
      {
        pattern: "Unauthorized",
        message: "There is a mismatch with our records. Please try again."
      }
    ]
  },
  Wallet: {
    byRegex: [
      {
        pattern: "Couldn't create card",
        message: (
          <>
            Unable to create card, please{" "}
            <ButtonContactSupport>contact support</ButtonContactSupport>
          </>
        )
      }
    ]
  },
  PasswordReset: {
    byRegex: [
      {
        pattern: "Session does not exist",
        message: "Your password reset link has expired"
      }
    ]
  },
  PaymentsSetup: {
    byRegex: [
      {
        pattern: "Account .* cannot be used for instant payouts",
        message:
          "This account isn't eligible for instant payouts. Please select a different account or choose a standard payout method."
      }
    ]
  },
  MonitoredEntity: {
    byRegex: [
      {
        pattern:
          "No valid changes were provided. Ensure you are providing a different tax ID",
        message:
          "No valid changes were provided. Ensure you are providing a different tax ID."
      },
      {
        pattern: /Invalid SSN|Invalid ITIN|Invalid EIN/,
        message: (
          <>
            We couldn&apos;t validate your tax ID. Please double-check your
            entry and try again.{" "}
            <ButtonContactSupport>Contact our support</ButtonContactSupport> if
            you need assistance.
          </>
        )
      },
      {
        pattern: /Validation of update failed/,
        message: (
          <>
            We couldn&apos;t verify your information. Please go back and
            double-check your entries, then try again.{" "}
            <ButtonContactSupport>Contact our support</ButtonContactSupport> if
            this issue continues.
          </>
        )
      }
    ]
  },
  Engagement: {
    byRegex: [
      {
        pattern: /An engagement with the name '(.*)' already exists/,
        message: "An engagement with the name '$1' already exists"
      }
    ]
  },
  InternalAccount: {
    byRegex: [
      {
        pattern: "(.*) creation is only available for US residents",
        message: "$1 creation is only available for US residents"
      }
    ]
  },
  CreatePayee: {
    byRegex: [
      {
        pattern:
          /Payee with external id (.*) already exists for this payer. Payee id: .*/,
        message:
          "A contractor with External ID '$1' already exists. Please choose a different External ID."
      }
    ]
  },
  UpdatePayee: {
    byRegex: [
      {
        pattern:
          /Payee with external id (.*) already exists for this payer. Payee id: .*/,
        message:
          "External ID '$1' already occupied for other contractor. Please choose a different External ID."
      }
    ]
  },
  PayoutSettings: {
    byRegex: [
      {
        pattern: "Account .* cannot be used for instant payouts",
        message:
          "This account isn't eligible for instant payouts. Please select a different account or choose a standard payout method."
      },
      {
        pattern: "Wingspan debit cards cannot be used for Instant payout",
        message: "Wingspan debit cards cannot be used for Instant payout"
      },
      {
        pattern: "Discover debit cards cannot be used for Instant payout",
        message: "Discover debit cards cannot be used for Instant payout"
      }
    ]
  },
  // Contexts carried over from the legacy WSErrorMessage registry that have
  // no mapped messages yet.
  BulkBatchImportError: {},
  BulkBatchStartImport: {},
  Bookkeeping: {},
  PayableForm: {},
  CreateClient: {},
  UpdateClient: {},
  ArchiveClient: {},
  RemoveClient: {},
  CreateCollaborator: {},
  CreateCustomField: {},
  UpdateCollaborator: {},
  EditContact: {},
  BenefitsAddZipCode: {},
  ActivateBenefit: {},
  BenefitsSetUp: {},
  CreateInvoiceWithPPL: {},
  PaymentMethodRequired: {},
  UploadLogo: {},
  CreatePPL: {},
  Unauthorized: {},
  CreateAccount: {},
  DisputeInvoice: {},
  IntegrationsQuickbooksMapping: {},
  EditCollaboratorIntegrations: {},
  EditClientIntegrations: {},
  RevealCardDataOPT: {},
  CreateCollaboratorsGroup: {},
  UpdateCollaboratorsGroup: {},
  DeleteCollaboratorsGroup: {},
  PayrollSettings: {},
  EditTemplate: {},
  RemoveEligibilityRequirement: {},
  PaymentsVerificationUpgrade: {},
  NEC1099Onboarding: {},
  BulkPayableBatchCreate: {},
  BulkBatchUpdate: {},
  NEC1099DetailsUpdate: {},
  NEC1099BulkCalculate1099: {},
  NEC1099Correction: {},
  NEC1099TaxDocuments: {},
  OffCyclePayroll: {},
  PayrollAction: {},
  BankAccountVerification: {},
  NEC1099AddRecipient: {},
  InvoiceRefund: {},
  NEC1099RequestCorrection: {},
  NEC1099PayerCorrection: {},
  NEC1099PayerUpdateCorrection: {},
  NEC1099PayerRejectCorrection: {},
  NEC1099PayerApproveCorrection: {},
  UploadDocument: {},
  DeleteDocument: {},
  UpdateInvoicingConfig: {},
  CreateEmailSnippet: {},
  OnboardingBusinessProfile: {},
  PayeeOnboardingConsentForms: {},
  ElectronicTaxFormConsent: {},
  FundingMethod: {},
  UpdateRequirement: {},
  AddPayeeToEngagement: {},
  EditPayerPayeeInternalNotes: {},
  Settings: {},
  TaxFilling: {},
  WorkLog: {},
  WorkItem: {},
  WorkDefiniton: {},
  Subscription: {},
  AccountSwitch: {
    default: "Sorry! An error occurred while switching accounts."
  },
  MfaChallenge: {
    default: "We couldn't verify that code. Please try again."
  },
  MfaFactor: {
    default:
      "Sorry! An error occurred while setting up this verification method."
  },
  TeamStakeholder: {
    default: "Sorry! An error occurred while managing this team member."
  },
  ApiKeySave: {
    default: "Sorry! An error occurred while saving the API key."
  },
  ApiKeyRotate: {
    default: "Sorry! An error occurred while rotating the API key."
  },
  ApiKeyDelete: {
    default: "Sorry! An error occurred while deleting the API key."
  }
} satisfies Record<string, ErrorMapEntry>;

export type ErrorContext = keyof typeof errorMap;

export const networkErrorMessage =
  "We're having trouble communicating with our servers. Please check your internet connection and try again!";

export const genericErrorMessage = (
  <>
    Sorry! Something went wrong here. Please try again or{" "}
    <ButtonContactSupport>contact our support</ButtonContactSupport> if this
    issue continues.
  </>
);
